import { useState, useEffect, useRef } from "react";

const COLORS = {
  forest: "#0D2B1F",
  emerald: "#1A7A4A",
  mint: "#4ECFA0",
  frost: "#EDF7F2",
  sage: "#6B8C7D",
  white: "#FFFFFF",
  dark: "#081811",
};

const TraxeloIcon = ({ size = 32, darkBg = true }) => {
  const pad = size * 0.28;
  const inner = size - 2 * pad;
  const ox = pad, oy = pad;
  const p1 = [ox, oy + inner];
  const p2 = [ox + inner * 0.35, oy + inner * 0.35];
  const p3 = [ox + inner * 0.58, oy + inner * 0.56];
  const p4 = [ox + inner, oy];
  const pts = `${p1[0]},${p1[1]} ${p2[0]},${p2[1]} ${p3[0]},${p3[1]} ${p4[0]},${p4[1]}`;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none">
      <rect width={size} height={size} rx={size * 0.18} fill={COLORS.emerald} />
      <polyline points={pts} stroke={COLORS.white} strokeWidth={size * 0.045} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={p4[0]} cy={p4[1]} r={size * 0.06} fill={COLORS.mint} />
    </svg>
  );
};

const Logo = ({ size = 32, textSize = 22, dark = true }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    <TraxeloIcon size={size} darkBg={dark} />
    <div>
      <div style={{ fontFamily: "'Syne', 'Helvetica Neue', sans-serif", fontWeight: 700, fontSize: textSize, lineHeight: 1, letterSpacing: "-0.02em", color: dark ? COLORS.white : COLORS.forest }}>
        Trax<span style={{ color: COLORS.mint }}>elo</span>
      </div>
      <div style={{ fontSize: textSize * 0.38, letterSpacing: "0.12em", color: COLORS.sage, marginTop: 2, fontWeight: 400 }}>TECH SOLUTIONS</div>
    </div>
  </div>
);

const services = [
  { icon: "🌐", title: "Websites & Web Apps", desc: "Scalable, responsive web applications built with modern frameworks — from landing pages to full SaaS platforms.", color: "#0F3A26" },
  { icon: "📱", title: "Mobile Apps", desc: "Cross-platform mobile apps for iOS and Android built with Flutter — fast, native-quality, and beautifully designed.", color: "#0A2E1E" },
  { icon: "💼", title: "Portfolio Services", desc: "Professional portfolio websites and digital presence that showcase your work and attract the right opportunities.", color: "#0F3A26" },
  { icon: "☁️", title: "Cloud Services", desc: "AWS-powered cloud infrastructure, DevOps pipelines, and scalable deployments that grow with your business.", color: "#0A2E1E" },
  { icon: "🔒", title: "Cyber Security", desc: "Security audits, vulnerability assessments, and solutions that protect your data and your customers.", color: "#0F3A26" },
  { icon: "🎨", title: "Branding Solutions", desc: "Complete brand identity design — logo, color systems, typography, and guidelines that make you unforgettable.", color: "#0A2E1E" },
  { icon: "🖨️", title: "Graphics & Printing", desc: "High-impact visual design for digital and print — flyers, banners, business cards, and marketing materials.", color: "#0F3A26" },
  { icon: "🤝", title: "Consultation", desc: "Strategic technology consulting to help you choose the right tools, architecture, and roadmap for your goals.", color: "#0A2E1E" },
  { icon: "📣", title: "Social Media Management", desc: "Content creation, scheduling, and community management that grows your audience and drives engagement.", color: "#0F3A26" },
  { icon: "🎬", title: "Videography", desc: "Professional video production, editing, and storytelling for brands, events, and digital campaigns.", color: "#0A2E1E" },
];

const stats = [
  { value: "50+", label: "Projects Delivered" },
  { value: "30+", label: "Happy Clients" },
  { value: "10+", label: "Services Offered" },
  { value: "3+", label: "Years Experience" },
];

const faqs = [
  { q: "How long does a typical project take?", a: "Timelines depend on scope. A simple website takes 2–4 weeks; a full mobile app typically takes 8–16 weeks. We always agree on a clear timeline before we start." },
  { q: "Do you work with startups?", a: "Absolutely. We love helping early-stage businesses build their digital foundation — from brand identity to their first web or mobile product." },
  { q: "What happens after my project launches?", a: "We offer ongoing maintenance, support, and growth services. You'll never be left without a technical partner after go-live." },
  { q: "Can you help if I only have an idea?", a: "Yes. Our consultation service is designed exactly for this — we'll help you validate, plan, and scope your idea into a buildable product." },
];

export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [formData, setFormData] = useState({ name: "", email: "", service: "", message: "" });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [visibleCards, setVisibleCards] = useState([]);
  const cardRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = parseInt(entry.target.dataset.idx);
            setVisibleCards((prev) => [...new Set([...prev, idx])]);
          }
        });
      },
      { threshold: 0.15 }
    );
    cardRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
    setActiveSection(id);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormSubmitted(true);
  };

  const navLinks = ["home", "services", "about", "faq", "contact"];

  return (
    <div style={{ fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif", background: COLORS.forest, color: COLORS.white, minHeight: "100vh", overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:wght@300;400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: #0D2B1F; } ::-webkit-scrollbar-thumb { background: #1A7A4A; border-radius: 2px; }
        .nav-link { background: none; border: none; color: #6B8C7D; font-family: 'DM Sans', sans-serif; font-size: 14px; cursor: pointer; padding: 6px 12px; border-radius: 6px; transition: color 0.2s, background 0.2s; }
        .nav-link:hover, .nav-link.active { color: #fff; background: rgba(78,207,160,0.08); }
        .card-hover { transition: transform 0.25s ease, border-color 0.25s ease; }
        .card-hover:hover { transform: translateY(-4px); border-color: #1A7A4A !important; }
        .fade-in { opacity: 0; transform: translateY(20px); transition: opacity 0.5s ease, transform 0.5s ease; }
        .fade-in.visible { opacity: 1; transform: translateY(0); }
        .mint-btn { background: #4ECFA0; color: #0D2B1F; border: none; padding: 12px 28px; border-radius: 8px; font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 500; cursor: pointer; transition: background 0.2s, transform 0.15s; display: inline-flex; align-items: center; gap: 8px; }
        .mint-btn:hover { background: #3abf90; transform: translateY(-1px); }
        .outline-btn { background: transparent; color: #fff; border: 1.5px solid #1A7A4A; padding: 11px 28px; border-radius: 8px; font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 500; cursor: pointer; transition: background 0.2s, border-color 0.2s; }
        .outline-btn:hover { background: rgba(78,207,160,0.08); border-color: #4ECFA0; }
        input, textarea, select { background: #0F3526; border: 1px solid #1A5C36; color: #fff; border-radius: 8px; padding: 11px 14px; font-family: 'DM Sans', sans-serif; font-size: 14px; width: 100%; outline: none; transition: border-color 0.2s; }
        input:focus, textarea:focus, select:focus { border-color: #4ECFA0; }
        input::placeholder, textarea::placeholder { color: #4A6B5A; }
        select option { background: #0D2B1F; }
        .hero-glow { position: absolute; width: 600px; height: 600px; border-radius: 50%; background: radial-gradient(circle, rgba(26,122,74,0.18) 0%, transparent 70%); pointer-events: none; }
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: flex !important; }
          .hero-grid { grid-template-columns: 1fr !important; }
          .services-grid { grid-template-columns: 1fr 1fr !important; }
          .stats-row { grid-template-columns: 1fr 1fr !important; }
          .contact-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 480px) {
          .services-grid { grid-template-columns: 1fr !important; }
          .stats-row { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      {/* ── NAV ── */}
      <nav style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(13,43,31,0.92)", backdropFilter: "blur(12px)", borderBottom: "1px solid rgba(26,122,74,0.3)", padding: "0 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button onClick={() => scrollTo("home")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            <Logo size={28} textSize={18} />
          </button>
          <div className="desktop-nav" style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {navLinks.map((l) => (
              <button key={l} className={`nav-link ${activeSection === l ? "active" : ""}`} onClick={() => scrollTo(l)}>
                {l.charAt(0).toUpperCase() + l.slice(1)}
              </button>
            ))}
            <button className="mint-btn" style={{ marginLeft: 12, padding: "8px 20px", fontSize: 13 }} onClick={() => scrollTo("contact")}>
              Get a Quote
            </button>
          </div>
          <button className="mobile-menu-btn" style={{ display: "none", background: "none", border: "1px solid #1A7A4A", borderRadius: 6, padding: "6px 10px", cursor: "pointer", color: COLORS.mint, fontSize: 20, alignItems: "center" }} onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? "✕" : "☰"}
          </button>
        </div>
        {menuOpen && (
          <div style={{ background: "#0A2118", borderTop: "1px solid rgba(26,122,74,0.3)", padding: "12px 24px 20px" }}>
            {navLinks.map((l) => (
              <button key={l} onClick={() => scrollTo(l)} style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", color: "#B8D4C4", fontSize: 15, padding: "10px 0", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                {l.charAt(0).toUpperCase() + l.slice(1)}
              </button>
            ))}
            <button className="mint-btn" style={{ marginTop: 12, width: "100%", justifyContent: "center" }} onClick={() => scrollTo("contact")}>Get a Quote</button>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section id="home" style={{ position: "relative", overflow: "hidden", minHeight: "92vh", display: "flex", alignItems: "center" }}>
        <div className="hero-glow" style={{ top: "-100px", right: "-100px" }} />
        <div className="hero-glow" style={{ bottom: "-200px", left: "-200px", opacity: 0.6 }} />
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "80px 24px", position: "relative", zIndex: 1, width: "100%" }}>
          <div className="hero-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60, alignItems: "center" }}>
            <div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(78,207,160,0.1)", border: "1px solid rgba(78,207,160,0.25)", borderRadius: 20, padding: "5px 14px", marginBottom: 24 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: COLORS.mint, display: "inline-block" }} />
                <span style={{ fontSize: 12, color: COLORS.mint, letterSpacing: "0.08em", fontWeight: 500 }}>ABUJA, NIGERIA</span>
              </div>
              <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: "clamp(36px, 5vw, 58px)", fontWeight: 800, lineHeight: 1.08, letterSpacing: "-0.03em", marginBottom: 20 }}>
                Intelligent Software<br />
                <span style={{ color: COLORS.mint }}>& Digital Solutions</span><br />
                that Drive Growth
              </h1>
              <p style={{ fontSize: 17, color: "#9ABFB0", lineHeight: 1.7, marginBottom: 36, maxWidth: 480 }}>
                Empowering businesses with intelligent software and digital solutions that drives growth. From web and mobile apps to cloud infrastructure and branding.
              </p>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <button className="mint-btn" onClick={() => scrollTo("services")}>
                  Explore Services <span style={{ fontSize: 16 }}>→</span>
                </button>
                <button className="outline-btn" onClick={() => scrollTo("contact")}>
                  Talk to Us
                </button>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div style={{ position: "relative", width: 320, height: 320 }}>
                {/* Animated ring */}
                <div style={{ position: "absolute", inset: 0, borderRadius: "50%", border: "1px dashed rgba(78,207,160,0.25)", animation: "spin 18s linear infinite" }} />
                <div style={{ position: "absolute", inset: 20, borderRadius: "50%", border: "1px dashed rgba(26,122,74,0.4)", animation: "spin 12s linear infinite reverse" }} />
                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } @keyframes pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.04); } }`}</style>
                {/* Center icon */}
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ background: "rgba(26,122,74,0.15)", border: "1px solid rgba(78,207,160,0.2)", borderRadius: "50%", padding: 36, animation: "pulse 4s ease-in-out infinite" }}>
                    <TraxeloIcon size={96} />
                  </div>
                </div>
                {/* Orbiting service dots */}
                {["🌐", "📱", "☁️", "🔒", "🎨", "📣"].map((icon, i) => {
                  const angle = (i / 6) * 2 * Math.PI - Math.PI / 2;
                  const r = 140;
                  const cx = 160 + r * Math.cos(angle);
                  const cy = 160 + r * Math.sin(angle);
                  return (
                    <div key={i} style={{ position: "absolute", left: cx - 20, top: cy - 20, width: 40, height: 40, borderRadius: "50%", background: "#0F3A26", border: "1px solid rgba(78,207,160,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>
                      {icon}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {/* Stats */}
          <div className="stats-row" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginTop: 64 }}>
            {stats.map((s) => (
              <div key={s.label} style={{ background: "rgba(15,53,38,0.6)", border: "1px solid rgba(26,122,74,0.3)", borderRadius: 12, padding: "18px 20px", textAlign: "center" }}>
                <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 28, fontWeight: 800, color: COLORS.mint }}>{s.value}</div>
                <div style={{ fontSize: 12, color: COLORS.sage, marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section id="services" style={{ padding: "96px 24px", background: "#081811" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <div style={{ display: "inline-block", background: "rgba(78,207,160,0.08)", border: "1px solid rgba(78,207,160,0.2)", borderRadius: 20, padding: "4px 16px", fontSize: 12, color: COLORS.mint, letterSpacing: "0.1em", fontWeight: 500, marginBottom: 16 }}>WHAT WE DO</div>
            <h2 style={{ fontFamily: "'Syne', sans-serif", fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 800, letterSpacing: "-0.02em", marginBottom: 14 }}>
              Services built for <span style={{ color: COLORS.mint }}>real business growth</span>
            </h2>
            <p style={{ fontSize: 16, color: COLORS.sage, maxWidth: 480, margin: "0 auto" }}>
              From your first idea to a scaled product — we have the expertise to take you there.
            </p>
          </div>
          <div className="services-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
            {services.map((s, i) => (
              <div key={i} data-idx={i} ref={(el) => (cardRefs.current[i] = el)}
                className={`card-hover fade-in ${visibleCards.includes(i) ? "visible" : ""}`}
                style={{ background: s.color, border: "1px solid rgba(26,122,74,0.35)", borderRadius: 14, padding: "24px 22px", transitionDelay: `${(i % 3) * 0.08}s` }}>
                <div style={{ fontSize: 28, marginBottom: 14 }}>{s.icon}</div>
                <h3 style={{ fontFamily: "'Syne', sans-serif", fontSize: 15, fontWeight: 700, marginBottom: 8, color: COLORS.white }}>{s.title}</h3>
                <p style={{ fontSize: 13.5, color: "#8CB5A0", lineHeight: 1.65 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ABOUT / WHY TRAXELO ── */}
      <section id="about" style={{ padding: "96px 24px", background: COLORS.forest, position: "relative", overflow: "hidden" }}>
        <div className="hero-glow" style={{ top: "-50px", left: "50%", transform: "translateX(-50%)", opacity: 0.5 }} />
        <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center" }} className="hero-grid">
            <div>
              <div style={{ display: "inline-block", background: "rgba(78,207,160,0.08)", border: "1px solid rgba(78,207,160,0.2)", borderRadius: 20, padding: "4px 16px", fontSize: 12, color: COLORS.mint, letterSpacing: "0.1em", fontWeight: 500, marginBottom: 20 }}>WHY TRAXELO</div>
              <h2 style={{ fontFamily: "'Syne', sans-serif", fontSize: "clamp(26px, 3.5vw, 38px)", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.15, marginBottom: 20 }}>
                We build software that <span style={{ color: COLORS.mint }}>actually works</span> for your business
              </h2>
              <p style={{ fontSize: 15.5, color: "#9ABFB0", lineHeight: 1.75, marginBottom: 28 }}>
                Traxelo Tech Solutions Limited is a Abuja-based software and digital solutions company. We combine technical depth with creative thinking to deliver products that are fast, reliable, and built to grow.
              </p>
              <p style={{ fontSize: 15.5, color: "#9ABFB0", lineHeight: 1.75, marginBottom: 36 }}>
                Whether you need a scalable web app, a cloud migration, a complete brand identity, or just a strategic partner to help you think through your next move — we're here.
              </p>
              <button className="mint-btn" onClick={() => scrollTo("contact")}>Start a Project →</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {[
                { icon: "⚡", title: "Fast delivery", body: "We ship in sprints, not months. You see real progress from week one." },
                { icon: "🎯", title: "Outcome-focused", body: "We measure success by your business results, not just deliverables." },
                { icon: "🔧", title: "Full-stack team", body: "Design, engineering, cloud, and content — all under one roof." },
                { icon: "🌍", title: "Nigeria-based, global standard", body: "Built and delivered from Abuja with world-class quality." },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", gap: 16, background: "rgba(15,53,38,0.5)", border: "1px solid rgba(26,122,74,0.3)", borderRadius: 12, padding: "18px 20px" }}>
                  <div style={{ fontSize: 22, flexShrink: 0, marginTop: 2 }}>{item.icon}</div>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 4, color: COLORS.white }}>{item.title}</div>
                    <div style={{ fontSize: 13.5, color: COLORS.sage, lineHeight: 1.6 }}>{item.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── PROCESS ── */}
      <section style={{ padding: "80px 24px", background: "#081811" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", textAlign: "center" }}>
          <div style={{ display: "inline-block", background: "rgba(78,207,160,0.08)", border: "1px solid rgba(78,207,160,0.2)", borderRadius: 20, padding: "4px 16px", fontSize: 12, color: COLORS.mint, letterSpacing: "0.1em", fontWeight: 500, marginBottom: 16 }}>HOW WE WORK</div>
          <h2 style={{ fontFamily: "'Syne', sans-serif", fontSize: "clamp(24px, 3.5vw, 36px)", fontWeight: 800, letterSpacing: "-0.02em", marginBottom: 48 }}>
            From first conversation to <span style={{ color: COLORS.mint }}>live product</span>
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0, position: "relative" }} className="stats-row">
            <div style={{ position: "absolute", top: "28px", left: "12.5%", right: "12.5%", height: 1, background: "rgba(78,207,160,0.2)", zIndex: 0 }} />
            {["Discovery", "Design", "Build", "Launch"].map((step, i) => (
              <div key={i} style={{ padding: "0 12px", position: "relative", zIndex: 1 }}>
                <div style={{ width: 56, height: 56, borderRadius: "50%", background: i === 1 ? COLORS.emerald : "#0F3526", border: `2px solid ${i === 1 ? COLORS.mint : "rgba(78,207,160,0.2)"}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 18, color: i === 1 ? COLORS.white : COLORS.mint }}>
                  {i + 1}
                </div>
                <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 15, marginBottom: 8 }}>{step}</div>
                <div style={{ fontSize: 13, color: COLORS.sage, lineHeight: 1.6 }}>
                  {["We listen, ask, and understand your goals.", "We prototype and get your approval before writing code.", "Agile sprints with regular demos and updates.", "We deploy, monitor, and hand over your product."][i]}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" style={{ padding: "96px 24px", background: COLORS.forest }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <div style={{ display: "inline-block", background: "rgba(78,207,160,0.08)", border: "1px solid rgba(78,207,160,0.2)", borderRadius: 20, padding: "4px 16px", fontSize: 12, color: COLORS.mint, letterSpacing: "0.1em", fontWeight: 500, marginBottom: 16 }}>FAQ</div>
            <h2 style={{ fontFamily: "'Syne', sans-serif", fontSize: "clamp(24px, 3.5vw, 36px)", fontWeight: 800, letterSpacing: "-0.02em" }}>
              Common questions
            </h2>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {faqs.map((f, i) => (
              <div key={i} style={{ background: "#0F3526", border: `1px solid ${openFaq === i ? "rgba(78,207,160,0.4)" : "rgba(26,122,74,0.3)"}`, borderRadius: 12, overflow: "hidden", transition: "border-color 0.2s" }}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{ width: "100%", background: "none", border: "none", color: COLORS.white, padding: "18px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", textAlign: "left", fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 500, gap: 12 }}>
                  <span>{f.q}</span>
                  <span style={{ fontSize: 18, color: COLORS.mint, flexShrink: 0, transition: "transform 0.2s", transform: openFaq === i ? "rotate(45deg)" : "none" }}>+</span>
                </button>
                {openFaq === i && (
                  <div style={{ padding: "0 20px 18px", fontSize: 14, color: "#8CB5A0", lineHeight: 1.7 }}>{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section id="contact" style={{ padding: "96px 24px", background: "#081811" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <div style={{ display: "inline-block", background: "rgba(78,207,160,0.08)", border: "1px solid rgba(78,207,160,0.2)", borderRadius: 20, padding: "4px 16px", fontSize: 12, color: COLORS.mint, letterSpacing: "0.1em", fontWeight: 500, marginBottom: 16 }}>CONTACT US</div>
            <h2 style={{ fontFamily: "'Syne', sans-serif", fontSize: "clamp(26px, 4vw, 42px)", fontWeight: 800, letterSpacing: "-0.02em", marginBottom: 12 }}>
              Let's build something <span style={{ color: COLORS.mint }}>great together</span>
            </h2>
            <p style={{ fontSize: 16, color: COLORS.sage, maxWidth: 440, margin: "0 auto" }}>
              Tell us about your project and we'll get back to you within 24 hours.
            </p>
          </div>
          <div className="contact-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 36, alignItems: "start" }}>
            {/* Info */}
            <div>
              <div style={{ background: "#0F3526", border: "1px solid rgba(26,122,74,0.3)", borderRadius: 16, padding: "28px 24px", marginBottom: 16 }}>
                <h3 style={{ fontFamily: "'Syne', sans-serif", fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Get in touch</h3>
                {[
                  { icon: "📍", label: "Location", value: "Abuja, FCT, Nigeria" },
                  { icon: "📧", label: "Email", value: "info@traxelotech.com" },
                  { icon: "📞", label: "Phone", value: "0810 959 6696" },
                  { icon: "🕐", label: "Response time", value: "Within 24 hours" },
                ].map((item) => (
                  <div key={item.label} style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 16 }}>
                    <span style={{ fontSize: 18 }}>{item.icon}</span>
                    <div>
                      <div style={{ fontSize: 11, color: COLORS.sage, letterSpacing: "0.08em", fontWeight: 500, marginBottom: 2 }}>{item.label.toUpperCase()}</div>
                      <div style={{ fontSize: 14, color: COLORS.white }}>{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ background: "#0F3526", border: "1px solid rgba(26,122,74,0.3)", borderRadius: 16, padding: "24px" }}>
                <div style={{ fontSize: 13, color: COLORS.sage, marginBottom: 14 }}>Follow us</div>
                <div style={{ display: "flex", gap: 10 }}>
                  {["LinkedIn", "Twitter", "Instagram"].map((s) => (
                    <div key={s} style={{ background: "rgba(78,207,160,0.08)", border: "1px solid rgba(78,207,160,0.2)", borderRadius: 8, padding: "6px 14px", fontSize: 12, color: COLORS.mint, cursor: "pointer" }}>{s}</div>
                  ))}
                </div>
              </div>
            </div>
            {/* Form */}
            <div style={{ background: "#0F3526", border: "1px solid rgba(26,122,74,0.3)", borderRadius: 16, padding: "32px 28px" }}>
              {formSubmitted ? (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
                  <h3 style={{ fontFamily: "'Syne', sans-serif", fontSize: 20, fontWeight: 700, marginBottom: 8, color: COLORS.mint }}>Message sent!</h3>
                  <p style={{ fontSize: 14, color: COLORS.sage }}>Thanks for reaching out. We'll get back to you within 24 hours.</p>
                  <button className="outline-btn" style={{ marginTop: 24 }} onClick={() => setFormSubmitted(false)}>Send another message</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                    <div>
                      <label style={{ display: "block", fontSize: 12, color: COLORS.sage, marginBottom: 6, letterSpacing: "0.06em" }}>FULL NAME</label>
                      <input required placeholder="Your name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 12, color: COLORS.sage, marginBottom: 6, letterSpacing: "0.06em" }}>EMAIL</label>
                      <input required type="email" placeholder="you@company.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                    </div>
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: "block", fontSize: 12, color: COLORS.sage, marginBottom: 6, letterSpacing: "0.06em" }}>SERVICE NEEDED</label>
                    <select required value={formData.service} onChange={(e) => setFormData({ ...formData, service: e.target.value })}>
                      <option value="">Select a service…</option>
                      {services.map((s) => <option key={s.title} value={s.title}>{s.title}</option>)}
                      <option value="Other">Other / Not sure yet</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: 22 }}>
                    <label style={{ display: "block", fontSize: 12, color: COLORS.sage, marginBottom: 6, letterSpacing: "0.06em" }}>YOUR MESSAGE</label>
                    <textarea required rows={5} placeholder="Tell us about your project, timeline, and budget…" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} style={{ resize: "vertical", minHeight: 120 }} />
                  </div>
                  <button className="mint-btn" type="submit" style={{ width: "100%", justifyContent: "center", padding: "14px", fontSize: 15 }}>
                    Send message →
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ background: COLORS.dark, borderTop: "1px solid rgba(26,122,74,0.3)", padding: "48px 24px 28px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 40, marginBottom: 48 }} className="stats-row">
            <div>
              <Logo size={26} textSize={17} />
              <p style={{ fontSize: 13.5, color: COLORS.sage, lineHeight: 1.7, marginTop: 14, maxWidth: 280 }}>
                Empowering businesses with intelligent software and digital solutions that drives growth.
              </p>
            </div>
            {[
              { heading: "Services", links: ["Web & Apps", "Mobile Apps", "Cloud Services", "Cyber Security"] },
              { heading: "Company", links: ["About Us", "Our Work", "Blog", "Careers"] },
              { heading: "Connect", links: ["LinkedIn", "Twitter", "Instagram", "info@traxelotech.com"] },
            ].map((col) => (
              <div key={col.heading}>
                <div style={{ fontSize: 12, color: COLORS.mint, letterSpacing: "0.1em", fontWeight: 500, marginBottom: 16 }}>{col.heading.toUpperCase()}</div>
                {col.links.map((l) => (
                  <div key={l} style={{ fontSize: 13.5, color: COLORS.sage, marginBottom: 10, cursor: "pointer" }}>{l}</div>
                ))}
              </div>
            ))}
          </div>
          <div style={{ borderTop: "1px solid rgba(26,122,74,0.2)", paddingTop: 20, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <div style={{ fontSize: 12.5, color: "#4A6B5A" }}>© 2025 Traxelo Tech Solutions Limited. All rights reserved.</div>
            <div style={{ display: "flex", gap: 20 }}>
              {["Privacy Policy", "Terms of Service"].map((l) => (
                <span key={l} style={{ fontSize: 12.5, color: "#4A6B5A", cursor: "pointer" }}>{l}</span>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

